import { toISODate, isScheduledOn, addDays } from './dates';

// Determine if a log counts as "successful" for streak purposes.
// 'done' always counts. 'skipped' is treated as a "freeze" (does not break streak,
// but does not extend it either). 'missed' breaks the streak.
const isSuccess = (log, habit) => {
  if (!log) return false;
  if (log.status === 'done') {
    if (habit.type === 'binary') return true;
    return Number(log.value || 0) >= Number(habit.target_value || 1);
  }
  if (log.status === 'partial') return false;
  return false;
};

const isFreeze = (log) => log && log.status === 'skipped';

// Build a map of date->log for quick lookup
const indexLogs = (logs) => {
  const m = {};
  for (const l of logs) m[l.log_date] = l;
  return m;
};

export function calculateStreaks(habit, logs) {
  if (!habit || !logs) return { current: 0, longest: 0 };
  const byDate = indexLogs(logs);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Walk back from today, skipping unscheduled days, freezing on skipped.
  let current = 0;
  let cursor = new Date(today);
  // If today is scheduled and not yet logged successful, don't break — start from yesterday.
  const todayKey = toISODate(today);
  const todayLog = byDate[todayKey];
  const todayScheduled = isScheduledOn(habit, today);
  if (todayScheduled) {
    if (isSuccess(todayLog, habit)) {
      current += 1;
      cursor = addDays(cursor, -1);
    } else if (isFreeze(todayLog)) {
      cursor = addDays(cursor, -1);
    } else {
      // Don't count today as broken yet — just start backwards from yesterday
      cursor = addDays(cursor, -1);
    }
  } else {
    cursor = addDays(cursor, -1);
  }

  // Cap walk-back at 5 years
  const maxBack = 365 * 5;
  let steps = 0;
  while (steps < maxBack) {
    const key = toISODate(cursor);
    if (!isScheduledOn(habit, cursor)) {
      cursor = addDays(cursor, -1);
      steps++;
      continue;
    }
    const log = byDate[key];
    if (isSuccess(log, habit)) {
      current += 1;
      cursor = addDays(cursor, -1);
      steps++;
      continue;
    }
    if (isFreeze(log)) {
      cursor = addDays(cursor, -1);
      steps++;
      continue;
    }
    break;
  }

  // Longest streak — scan all logs chronologically
  const sorted = [...logs].sort((a, b) => a.log_date.localeCompare(b.log_date));
  let longest = 0;
  let run = 0;
  let lastDate = null;
  for (const log of sorted) {
    if (!isSuccess(log, habit)) {
      run = 0;
      lastDate = log.log_date;
      continue;
    }
    if (lastDate) {
      // count gap days; if all gap days are unscheduled or freezes, keep run
      let d = new Date(lastDate);
      d = addDays(d, 1);
      let broken = false;
      while (toISODate(d) < log.log_date) {
        const key = toISODate(d);
        if (isScheduledOn(habit, d)) {
          const l = byDate[key];
          if (!isFreeze(l)) { broken = true; break; }
        }
        d = addDays(d, 1);
      }
      if (broken) run = 0;
    }
    run += 1;
    longest = Math.max(longest, run);
    lastDate = log.log_date;
  }
  longest = Math.max(longest, current);
  return { current, longest };
}

export function completionRate(habit, logs, days = 30) {
  if (!habit) return 0;
  let scheduled = 0;
  let done = 0;
  const byDate = indexLogs(logs);
  let d = new Date();
  d.setHours(0, 0, 0, 0);
  for (let i = 0; i < days; i++) {
    if (isScheduledOn(habit, d)) {
      scheduled += 1;
      const log = byDate[toISODate(d)];
      if (isSuccess(log, habit)) done += 1;
    }
    d = addDays(d, -1);
  }
  return scheduled === 0 ? 0 : Math.round((done / scheduled) * 100);
}

export const getLogIntensity = (habit, log) => {
  if (!log) return 0;
  if (log.status === 'skipped') return 1;
  if (log.status === 'missed') return 0;
  if (habit.type === 'binary') return 4;
  const ratio = Number(log.value || 0) / Math.max(Number(habit.target_value || 1), 1);
  if (ratio >= 1) return 4;
  if (ratio >= 0.66) return 3;
  if (ratio >= 0.33) return 2;
  if (ratio > 0) return 1;
  return 0;
};
